import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@hoteltradergit/user-management/auth";
import HTLoader from "../../components/common/ht-loader/HTLoader";
import { userManagementApiClient } from "../../api/userManagementApiClient";
import { parsePermissions } from "../../rbac";
import { storeUserInitData } from "../../utils/userInitStorage";
import { resolvePostLoginRoute } from "../../utils/routeUtils";
import {
  getLogger,
  trackHeapEvent,
  heapIdentify,
  setHeapUserProperties,
} from "@hoteltradergit/ht-telemetry-ui-lib";
import { ANALYTICS_EVENTS } from "../../constants/analyticsEvents";

const PROGRESS_STEPS = [
  "Loading...",
  "Getting profile...",
  "Checking permissions...",
  "Almost there...",
];

export function Callback() {
  const {
    idToken,
    logOut,
    isLoading: loginInProgress,
    error: loginError,
  } = useAuth();
  const navigate = useNavigate();
  const [progressText, setProgressText] = useState(PROGRESS_STEPS[0]);
  const hasStartedInit = useRef(false);

  // Animated progress text
  useEffect(() => {
    let step = 0;
    const interval = setInterval(() => {
      step = (step + 1) % PROGRESS_STEPS.length;
      setProgressText(PROGRESS_STEPS[step]);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // User init on idToken available
  useEffect(() => {
    if (!idToken || hasStartedInit.current) return;
    hasStartedInit.current = true;

    const initUser = async () => {
      try {
        const response = await userManagementApiClient.getUserPermissions();
        const user = response?.data?.data || response?.data;

        if (user) {
          // Activate pending users on first login
          if (user.status?.toUpperCase() === "PENDING") {
            try {
              await userManagementApiClient.activateUser();
            } catch (error) {
              getLogger().error("Failed to activate user on first login:", {
                type: "Error",
                page_url: window.location.href,
                error_desc: error,
              });
            }
          }

          const { allowSet, denySet } = parsePermissions(user.rolePermissions);
          const userInfo = {
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            imageUrl: user.profileImageSmall || "",
          };

          storeUserInitData({
            userInfo,
            allowSet,
            denySet,
            rolePermissions: user.rolePermissions ?? [],
          });

          // Track successful login with analytics. The lib guards against
          // heap.js being absent, so no window.heap checks are needed.
          heapIdentify(user.email);
          setHeapUserProperties({ userId: user.userId });
          trackHeapEvent(ANALYTICS_EVENTS.LOGIN_SUCCESS, {
            userId: user.userId,
          });

          const redirectPath = resolvePostLoginRoute(allowSet, denySet);
          navigate(redirectPath);
        } else {
          navigate("/");
        }
      } catch (error) {
        getLogger().error("Callback init failed, falling back:", {
          type: "Error",
          page_url: window.location.href,
          error_desc: error,
        });
        navigate("/");
      }
    };

    initUser();
  }, [idToken, navigate]);

  // Login error handling
  useEffect(() => {
    if (!loginInProgress && loginError) {
      toast.error("You are not authorized to login into this application.");
      setTimeout(() => {
        logOut();
      }, 1500);
    } else if (!loginInProgress && !idToken) {
      navigate("/auth");
    }
  }, [loginInProgress, loginError, idToken, navigate, logOut]);

  return <HTLoader loaderText={progressText} />;
}
